import { Autocomplete, TextField, Box, Typography, Chip, Divider } from '@mui/material';
import { X, Trash2 } from 'lucide-react';
import { getWarehousesThunk } from '@redux/features/warehouse.slice';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getProductsThunk } from '@redux/features/product.slice';
import { createBatchThunk } from '@redux/features/batch.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { Product } from '@core/models/product.model';
import { CreateBatchBody, ProductBatch } from '@core/models/batch.model';
import { Supplier } from '@core/models/supplier.model';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { resolveUploadFileUrl } from 'helpers/resolveUploadFileUrl';
import PackageDataMatrixScanField from 'components/batch/PackageDataMatrixScanField';

type IProps = {
    show: boolean;
    handleClose: () => void;
};

interface ProductBatchForm extends ProductBatch {
    product?: Product;
    isCompleted: boolean;
}

const AddBatchModal: FC<IProps> = ({ show, handleClose }) => {
    const { warehouses } = useAppSelector((state: RootState) => state.warehouseState);
    const { suppliers } = useAppSelector((state: RootState) => (state as any).supplierState || { suppliers: [] });
    const { products } = useAppSelector((state: RootState) => state.productState);
    const { createLoading } = useAppSelector((state: RootState) => state.batchState);

    const dispatch = useAppDispatch();
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [productBatches, setProductBatches] = useState<ProductBatchForm[]>([]);

    // ----------------------------------------------------------------
    // Helper function to check if product is RX category
    const isRXProduct = (product: Product | undefined): boolean => {
        if (!product) return false;
        // Check both category.categoryName and subCategories.categoryName for compatibility
        const categoryName = (product as any).category?.categoryName || (product as any).subCategories?.categoryName;
        return categoryName?.toUpperCase() === 'RX';
    };

    const validationSchema = Yup.object({
        warehouseId: Yup.number().required('Warehouse is required').min(1, 'Please select a warehouse'),
        prefferedSupplierId: Yup.number()
            .required('Preferred Supplier is required')
            .min(1, 'Please select a preferred supplier'),
        lotNumber: Yup.string().required('Lot Number is required'),
        packagingInfo: Yup.string().required('Packaging Info is required'),
        purchaseOrderNumber: Yup.string().required('Purchase Order Number is required'),
        comment: Yup.string().required('Comment is required'),
    });

    const handleSubmit = (values: any) => {
        if (productBatches.length === 0) {
            alert('Please add at least one product batch');
            return;
        }

        // Validate all product batches are completed
        const incompleteBatches = productBatches.filter((pb) => !pb.isCompleted);
        if (incompleteBatches.length > 0) {
            alert('Please complete all product batch forms before submitting');
            return;
        }

        const body: CreateBatchBody = {
            warehouseId: values.warehouseId,
            prefferedSupplierId: values.prefferedSupplierId,
            lotNumber: values.lotNumber,
            packagingInfo: values.packagingInfo,
            purchaseOrderNumber: values.purchaseOrderNumber,
            comment: values.comment,
            productBatches: productBatches.map((pb) => {
                const isRX = isRXProduct(pb.product);
                const serials = pb.serializedItems
                    .map((s) => ({ serialNumber: s.serialNumber.trim() }))
                    .filter((s) => s.serialNumber !== '');
                return {
                    productId: pb.productId,
                    lotNumber: pb.lotNumber,
                    expiryDate: pb.expiryDate,
                    actualPrice: pb.actualPrice,
                    batchSize: pb.batchSize,
                    packageBarcode: pb.packageBarcode,
                    // RX quantity is driven by inventory receive events (one per serial), not this field.
                    quantity: isRX ? 0 : pb.quantity,
                    serializedItems: serials,
                    pallet: pb.pallet,
                };
            }),
        };

        dispatch(createBatchThunk(body)).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                formik.resetForm();
                setProductBatches([]);
                setSelectedProductId(null);
                handleClose();
            }
        });
    };

    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            warehouseId: 0,
            prefferedSupplierId: 0,
            lotNumber: ``,
            packagingInfo: ``,
            purchaseOrderNumber: ``,
            comment: ``,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    // --------------------------------------------------
    useEffect(() => {
        if (show) {
            dispatch(getWarehousesThunk());
            dispatch(getSuppliersThunk());
            dispatch(getProductsThunk({ page: 0, productSize: 1000 }));
        }
    }, [show, dispatch]);

    // --------------------------------------------------
    type ProductWithPhoto = Product & {
        productPhoto?: {
            id: number | null;
            productId: number;
            photoPath: string;
        };
    };

    const productList: ProductWithPhoto[] = (products?.content || []) as ProductWithPhoto[];

    const getImageUrl = (photoPath: string | undefined | null) => resolveUploadFileUrl(photoPath);

    // --------------------------------------------------
    const handleAddProduct = () => {
        if (!selectedProductId) {
            alert('Please select a product first');
            return;
        }

        const product = productList.find((p) => p.id === selectedProductId);
        if (!product) return;

        // Check if product already added
        if (productBatches.some((pb) => pb.productId === selectedProductId)) {
            alert('This product has already been added');
            setSelectedProductId(null);
            return;
        }

        const newProductBatch: ProductBatchForm = {
            productId: selectedProductId,
            lotNumber: ``,
            expiryDate: ``,
            actualPrice: 0,
            batchSize: 0,
            packageBarcode: ``,
            quantity: 0,
            serializedItems: [],
            pallet: {
                sscc: ``,
            },
            product: product,
            isCompleted: false,
        };

        setProductBatches([...productBatches, newProductBatch]);
        setSelectedProductId(null);
    };

    const handleRemoveProductBatch = (index: number) => {
        setProductBatches(productBatches.filter((_, i) => i !== index));
    };

    // Helper function to validate if a product batch is completed
    const validateProductBatchCompletion = (pb: ProductBatchForm): boolean => {
        const isRX = isRXProduct(pb.product);

        // For RX products: quantity is not required, serialized items are required
        // For non-RX products: quantity is required, serialized items are not required
        return (
            pb.lotNumber.trim() !== '' &&
            pb.expiryDate.trim() !== '' &&
            pb.actualPrice > 0 &&
            pb.batchSize > 0 &&
            pb.packageBarcode.trim() !== '' &&
            pb.pallet.sscc.trim() !== '' &&
            (isRX
                ? pb.serializedItems.length > 0 && pb.serializedItems.every((item) => item.serialNumber.trim() !== '')
                : pb.quantity >= 0)
        );
    };

    const handleUpdateProductBatch = (index: number, field: keyof ProductBatchForm, value: any) => {
        const updated = [...productBatches];
        updated[index] = { ...updated[index], [field]: value };

        // Validate if this product batch is completed
        updated[index].isCompleted = validateProductBatchCompletion(updated[index]);
        setProductBatches(updated);
    };

    const handleAddSerializedItem = (index: number) => {
        const updated = [...productBatches];
        updated[index].serializedItems.push({ serialNumber: '' });
        // Re-validate completion status after adding serialized item
        updated[index].isCompleted = validateProductBatchCompletion(updated[index]);
        setProductBatches(updated);
    };

    const handleAddScannedSerial = (index: number, serial: string) => {
        const updated = [...productBatches];
        if (updated[index].serializedItems.some((item) => item.serialNumber.trim() === serial.trim())) {
            return;
        }
        updated[index].serializedItems.push({ serialNumber: serial });
        updated[index].isCompleted = validateProductBatchCompletion(updated[index]);
        setProductBatches(updated);
    };

    const handleRemoveSerializedItem = (productBatchIndex: number, serialIndex: number) => {
        const updated = [...productBatches];
        updated[productBatchIndex].serializedItems = updated[productBatchIndex].serializedItems.filter(
            (_, i) => i !== serialIndex
        );
        // Re-validate completion status after removing serialized item
        updated[productBatchIndex].isCompleted = validateProductBatchCompletion(updated[productBatchIndex]);
        setProductBatches(updated);
    };

    const handleUpdateSerializedItem = (productBatchIndex: number, serialIndex: number, value: string) => {
        const updated = [...productBatches];
        updated[productBatchIndex].serializedItems[serialIndex].serialNumber = value;
        // Re-validate completion status after updating serialized item
        updated[productBatchIndex].isCompleted = validateProductBatchCompletion(updated[productBatchIndex]);
        setProductBatches(updated);
    };

    // --------------------------------------------------

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[1200px] md:max-h-[90vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Add Batch</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    {/* Basic Batch Information */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Autocomplete
                            disabled={createLoading}
                            options={warehouses || []}
                            getOptionLabel={(option) => option.name || 'Unnamed Warehouse'}
                            value={warehouses.find((w: any) => w.id === formik.values.warehouseId) || null}
                            onChange={(_, newValue) => {
                                formik.setFieldValue('warehouseId', newValue?.id || 0);
                            }}
                            onBlur={() => formik.setFieldTouched('warehouseId', true)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Warehouse Supplier *"
                                    size="small"
                                    error={formik.touched.warehouseId && Boolean(formik.errors.warehouseId)}
                                    helperText={formik.touched.warehouseId && formik.errors.warehouseId}
                                />
                            )}
                            sx={{ width: '100%' }}
                        />

                        <Autocomplete
                            disabled={createLoading}
                            options={suppliers || []}
                            getOptionLabel={(option) => option.businessName || option.email || 'Unnamed Supplier'}
                            value={
                                (suppliers as Supplier[]).find((s) => s.id === formik.values.prefferedSupplierId) ||
                                null
                            }
                            onChange={(_, newValue) => {
                                formik.setFieldValue('prefferedSupplierId', newValue?.id || 0);
                            }}
                            onBlur={() => formik.setFieldTouched('prefferedSupplierId', true)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Preferred Supplier *"
                                    size="small"
                                    error={
                                        formik.touched.prefferedSupplierId && Boolean(formik.errors.prefferedSupplierId)
                                    }
                                    helperText={formik.touched.prefferedSupplierId && formik.errors.prefferedSupplierId}
                                />
                            )}
                            sx={{ width: '100%' }}
                        />

                        <TextField
                            disabled={createLoading}
                            fullWidth
                            size="small"
                            id="lotNumber"
                            name="lotNumber"
                            label="Lot Number *"
                            value={formik.values.lotNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.lotNumber && Boolean(formik.errors.lotNumber)}
                            helperText={formik.touched.lotNumber && formik.errors.lotNumber}
                        />

                        <TextField
                            disabled={createLoading}
                            fullWidth
                            size="small"
                            id="packagingInfo"
                            name="packagingInfo"
                            label="Item Description *"
                            value={formik.values.packagingInfo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.packagingInfo && Boolean(formik.errors.packagingInfo)}
                            helperText={formik.touched.packagingInfo && formik.errors.packagingInfo}
                        />

                        <TextField
                            disabled={createLoading}
                            fullWidth
                            size="small"
                            id="purchaseOrderNumber"
                            name="purchaseOrderNumber"
                            label="Purchase Order Number *"
                            value={formik.values.purchaseOrderNumber}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.purchaseOrderNumber && Boolean(formik.errors.purchaseOrderNumber)}
                            helperText={formik.touched.purchaseOrderNumber && formik.errors.purchaseOrderNumber}
                        />

                        <TextField
                            disabled={createLoading}
                            fullWidth
                            size="small"
                            id="comment"
                            name="comment"
                            label="Comment *"
                            multiline
                            rows={2}
                            value={formik.values.comment}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.comment && Boolean(formik.errors.comment)}
                            helperText={formik.touched.comment && formik.errors.comment}
                        />
                    </div>

                    <Divider />

                    {/* Product Selection */}
                    <div className="flex flex-col gap-3">
                        <Typography variant="h6" className="font-semibold text-[#bd902e]">
                            Product Batches
                        </Typography>

                        <Autocomplete
                            disabled={createLoading}
                            options={productList.filter((p) => !productBatches.some((pb) => pb.productId === p.id))}
                            getOptionLabel={(option) => option.itemName || 'Unnamed Product'}
                            value={productList.find((p) => p.id === selectedProductId) || null}
                            onChange={(_, newValue) => {
                                setSelectedProductId(newValue?.id || null);
                            }}
                            renderOption={(props, option) => (
                                <Box
                                    component="li"
                                    {...props}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 1.5,
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            flexShrink: 0,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            backgroundColor: 'grey.200',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {option.productPhoto?.photoPath ? (
                                            <img
                                                src={getImageUrl(option.productPhoto.photoPath)}
                                                alt={option.itemName || 'Product'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No Image
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {option.itemName || 'Unnamed Product'}
                                        </Typography>
                                        {option.brandName && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                Brand: {option.brandName}
                                            </Typography>
                                        )}
                                        {option.unitPrice !== undefined && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                            >
                                                Price: ${option.unitPrice}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => <TextField {...params} label="Select Product" size="small" />}
                            sx={{ width: '100%' }}
                        />

                        <ButtonBase
                            innerText="Add Product"
                            onClick={handleAddProduct}
                            disabled={!selectedProductId || createLoading}
                            className="w-fit"
                            type="button"
                        />
                    </div>

                    {/* Product Batches Forms */}
                    <div className="flex flex-col gap-4">
                        {productBatches.map((productBatch, index) => (
                            <div
                                key={index}
                                className={`border-2 rounded-lg p-4 ${
                                    productBatch.isCompleted
                                        ? 'border-green-300 bg-green-50'
                                        : 'border-yellow-300 bg-yellow-50'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="h6" className="font-semibold">
                                            {productBatch.product?.itemName || 'Product'}
                                        </Typography>
                                        {productBatch.isCompleted ? (
                                            <Chip label="Completed" color="success" size="small" />
                                        ) : (
                                            <Chip label="Incomplete" color="warning" size="small" />
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveProductBatch(index)}
                                        disabled={createLoading}
                                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Lot Number *"
                                        value={productBatch.lotNumber}
                                        onChange={(e) => handleUpdateProductBatch(index, 'lotNumber', e.target.value)}
                                    />

                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Expiry Date *"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={productBatch.expiryDate}
                                        onChange={(e) => handleUpdateProductBatch(index, 'expiryDate', e.target.value)}
                                    />

                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Actual Price *"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        value={productBatch.actualPrice || ''}
                                        onChange={(e) =>
                                            handleUpdateProductBatch(
                                                index,
                                                'actualPrice',
                                                parseFloat(e.target.value) || 0
                                            )
                                        }
                                    />

                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Batch Size *"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        value={productBatch.batchSize || ''}
                                        onChange={(e) =>
                                            handleUpdateProductBatch(index, 'batchSize', parseInt(e.target.value) || 0)
                                        }
                                    />

                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Package Barcode *"
                                        value={productBatch.packageBarcode}
                                        onChange={(e) =>
                                            handleUpdateProductBatch(index, 'packageBarcode', e.target.value)
                                        }
                                    />

                                    {!isRXProduct(productBatch.product) && (
                                        <TextField
                                            disabled={createLoading}
                                            fullWidth
                                            size="small"
                                            label="Quantity *"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={productBatch.quantity || ''}
                                            onChange={(e) =>
                                                handleUpdateProductBatch(
                                                    index,
                                                    'quantity',
                                                    parseInt(e.target.value) || 0
                                                )
                                            }
                                        />
                                    )}

                                    <TextField
                                        disabled={createLoading}
                                        fullWidth
                                        size="small"
                                        label="Pallet SSCC *"
                                        value={productBatch.pallet.sscc}
                                        onChange={(e) =>
                                            handleUpdateProductBatch(index, 'pallet', {
                                                ...productBatch.pallet,
                                                sscc: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                {/* Serialized Items - Only show for RX products */}
                                {isRXProduct(productBatch.product) && (
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <Typography variant="subtitle2" className="font-semibold">
                                                Serialized Items *
                                            </Typography>
                                            <ButtonBase
                                                innerText="Add Serial Manually"
                                                onClick={() => handleAddSerializedItem(index)}
                                                disabled={createLoading}
                                                className="w-fit text-xs"
                                                type="button"
                                            />
                                        </div>

                                        {productBatch.productId != null && (
                                            <div className="mb-3">
                                                <PackageDataMatrixScanField
                                                    productId={productBatch.productId}
                                                    disabled={createLoading}
                                                    existingSerials={productBatch.serializedItems.map(
                                                        (s) => s.serialNumber
                                                    )}
                                                    onValidSerial={(serial) =>
                                                        handleAddScannedSerial(index, serial)
                                                    }
                                                />
                                            </div>
                                        )}

                                        <div className="flex flex-col gap-2">
                                            {productBatch.serializedItems.map((serial, serialIndex) => (
                                                <div key={serialIndex} className="flex items-center gap-2">
                                                    <TextField
                                                        disabled={createLoading}
                                                        fullWidth
                                                        size="small"
                                                        label={`Serial Number ${serialIndex + 1} *`}
                                                        value={serial.serialNumber}
                                                        onChange={(e) =>
                                                            handleUpdateSerializedItem(
                                                                index,
                                                                serialIndex,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSerializedItem(index, serialIndex)}
                                                        disabled={createLoading}
                                                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                            ))}
                                            {productBatch.serializedItems.length === 0 && (
                                                <Typography variant="caption" className="text-gray-500">
                                                    No serialized items added. Click "Add Serial Number" to add one.
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {productBatches.length === 0 && (
                            <Typography variant="body2" className="text-gray-500 text-center py-4">
                                No products added yet. Select a product above and click "Add Product" to start.
                            </Typography>
                        )}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                    disabled={createLoading}
                />
                <ButtonBase
                    innerText={createLoading ? 'Creating...' : 'Create Batch'}
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => formik.handleSubmit()}
                    disabled={createLoading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default AddBatchModal;
